import React, { useState } from 'react';
import { db, auth } from '../config/firebase';
import { doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { createUserWithEmailAndPassword, deleteUser } from "firebase/auth";
import { 
  Building2, User, Lock, CheckCircle, ArrowLeft, MapPin, Phone, Hash, Car, Map, Loader2 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import './RegisterScreen.css';

export default function RegisterScreen() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    responsibleName: '',
    companyName: '',
    address: '',
    cpf: '',
    cnpj: '',
    phone: '',
    fleetSize: '',
    routeCount: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      alert("As senhas não coincidem!");
      return;
    }

    setLoading(true);
    let createdUser = null;

    try {
      // 1. Criar o usuário no Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
      createdUser = userCredential.user;

      // 2. Tentar salvar no Firestore
      await setDoc(doc(db, "companies", createdUser.uid), {
        responsibleName: formData.responsibleName,
        companyName: formData.companyName,
        address: formData.address,
        cpf: formData.cpf,
        cnpj: formData.cnpj,
        phone: formData.phone,
        fleetSize: Number(formData.fleetSize || 0),
        routeCount: Math.max(0, Number(formData.routeCount || 0)),
        email: formData.email,
        active: false,
        status: 'pending',
        createdAt: serverTimestamp()
      });

      alert("Solicitação enviada com sucesso! Aguarde a aprovação da administração.");
      navigate('/login');

    } catch (error) {
      console.error("Erro no cadastro:", error);
      
      // 3. SE falhou ao salvar no banco, mas criou o usuário no Auth, 
      // precisamos apagar o usuário do Auth para ele poder tentar de novo.
      if (createdUser && error.code !== 'auth/email-already-in-use') {
        await deleteUser(createdUser).catch(err => console.error("Erro ao limpar Auth:", err));
      }

      // Tratamento de mensagens amigáveis
      if (error.code === 'auth/email-already-in-use') {
        alert("Este e-mail já está cadastrado como parceiro.");
      } else if (error.code === 'auth/weak-password') {
        alert("A senha deve ter pelo menos 6 caracteres.");
      } else {
        alert("Erro ao processar cadastro: " + error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-card">
        <header className="register-fixed-header">
          <button type="button" className="back-btn" onClick={() => navigate('/login')}>
            <ArrowLeft size={18} /> Voltar
          </button>
          <h2>Cadastro de Parceiro</h2>
          <p>Sua empresa passará por uma análise antes da liberação.</p>
        </header>

        <form className="register-form" onSubmit={handleSubmit}>
          <div className="reg-form-scroll">
            
            {/* Seção 1: Empresa */}
            <div className="reg-section">
              <h3 className="reg-section-title"><Building2 size={16} /> Identificação da Empresa</h3>
              <div className="reg-input-group">
                <label>Nome da Empresa</label>
                <input type="text" name="companyName" value={formData.companyName} onChange={handleChange} required placeholder="Razão Social" />
              </div>
              <div className="reg-grid-row">
                <div className="reg-input-group">
                  <label><Hash size={14}/> CNPJ</label>
                  <input type="text" name="cnpj" value={formData.cnpj} onChange={handleChange} required placeholder="00.000.000/0000-00" />
                </div>
                <div className="reg-input-group">
                  <label><Phone size={14}/> Telefone/WhatsApp</label>
                  <input type="text" name="phone" value={formData.phone} onChange={handleChange} required placeholder="(00) 00000-0000" />
                </div>
              </div>
            </div>

            {/* Seção 2: Responsável e Operação */}
            <div className="reg-section">
              <h3 className="reg-section-title"><User size={16} /> Responsável e Frota</h3>
              <div className="reg-grid-row">
                <div className="reg-input-group">
                  <label>Nome do Responsável</label>
                  <input type="text" name="responsibleName" value={formData.responsibleName} onChange={handleChange} required placeholder="Nome Completo" />
                </div>
                <div className="reg-input-group">
                  <label>CPF do Motorista</label>
                  <input type="text" name="cpf" value={formData.cpf} onChange={handleChange} required placeholder="000.000.000-00" />
                </div>
              </div>
              <div className="reg-grid-row">
                <div className="reg-input-group">
                  <label><Car size={14}/> Qtd. de Carros</label>
                  <input type="number" name="fleetSize" value={formData.fleetSize} onChange={handleChange} required placeholder="0" />
                </div>
                <div className="reg-input-group">
                  <label><Map size={14}/> Qtd. de Rotas</label>
                  <input type="number" name="routeCount" value={formData.routeCount} onChange={handleChange} required placeholder="0" />
                </div>
              </div>
            </div>

            {/* Seção 3: Segurança */}
            <div className="reg-section">
              <h3 className="reg-section-title"><Lock size={16} /> Credenciais de Acesso</h3>
              <div className="reg-input-group">
                <label>E-mail de Login</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="exemplo@empresa.com" />
              </div>
              <div className="reg-grid-row">
                <div className="reg-input-group">
                  <label>Senha</label>
                  <input type="password" name="password" value={formData.password} onChange={handleChange} required />
                </div>
                <div className="reg-input-group">
                  <label>Confirmar Senha</label>
                  <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required />
                </div>
              </div>
            </div>
          </div>

          <div className="reg-footer">
            <button type="submit" className="reg-submit-btn" disabled={loading}>
              {loading ? <Loader2 className="animate-spin" /> : <><CheckCircle size={20} /> Finalizar Cadastro</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}